import { useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import {
  initialQueryState,
  OptionsHelper,
  Permission,
  SituacaoNota,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useAuth } from '../../../../../auth'
import { useT } from '_metronic/i18n/Metronici18n'

export const NotaListSearchComponent = () => {
  const { t } = useT()
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { currentUser } = useAuth()
  const { idNota } = useParams<{ idNota: string }>()
  const location = useLocation()
  const isProfissionalRoute = location.pathname.includes('/profissional')
  const total = response?.totalElements ?? 0

  const isAdmin = currentUser?.grupo?.nome === Permission.Admin

  const debouncedFilters = useDebounce(filters, 500)

  // /notas/profissional/:idNota abre filtrada naquela nota.
  const search = useMemo(
    () =>
      [
        idNota ? `id:${idNota}` : '',
        debouncedFilters.ano ? `mesAno.ano:${debouncedFilters.ano.value}` : '',
        debouncedFilters.mes ? `mesAno.mes:${debouncedFilters.mes.value}` : '',
        debouncedFilters.empresa ? `fatura.empresa:${debouncedFilters.empresa.value}` : '',
        debouncedFilters.situacao ? `situacao:${debouncedFilters.situacao.value}` : '',
        debouncedFilters.profissional
          ? `profissional.nome:${debouncedFilters.profissional.label}`
          : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters, idNota]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.mes && {
      key: 'mes',
      label: t('mes.label'),
      value: filters.mes.label,
      onRemove: () => setFilter('mes', undefined),
    },
    filters.ano && {
      key: 'ano',
      label: t('ano.label'),
      value: filters.ano.label,
      onRemove: () => setFilter('ano', undefined),
    },
    filters.situacao && {
      key: 'situacao',
      label: t('situacao.label'),
      value: filters.situacao.label,
      onRemove: () => setFilter('situacao', undefined),
    },
    filters.profissional && {
      key: 'profissional',
      label: t('profissional.label'),
      value: filters.profissional.label,
      onRemove: () => setFilter('profissional', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  const moreFiltersActiveCount = [filters.situacao, filters.profissional].filter(Boolean).length

  return (
    <TableFilters
      moreFiltersActiveCount={moreFiltersActiveCount}
      moreFilters={
        <>
          <TableFilter label={t('situacao.label')}>
            <CustomSelect
              entity='situacao'
              onChange={(situacao?: IOption<SituacaoNota>) =>
                setFilter('situacao', situacao ?? undefined)
              }
              fetchOptions={OptionsHelper.getOptionsSituacaoNota}
              value={filters.situacao}
            />
          </TableFilter>
          {!isProfissionalRoute && (
            <TableFilter label={t('profissional.label')}>
              <CustomSelect
                entity='profissional'
                onChange={(profissional?: IOption<string>) =>
                  setFilter('profissional', profissional ?? undefined)
                }
                fetchOptions={OptionsHelper.getOptionsProfissionalSaudeAdmin}
                value={filters.profissional}
              />
            </TableFilter>
          )}
        </>
      }
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isAdmin && !filters.empresa
              ? undefined
              : isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'nota encontrada' : 'notas encontradas'}`
          }
        />
      }
    >
      <TableFilter label={t('empresa.label')} wide>
        <CustomSelect
          entity='empresa'
          onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEmpresasForFaturamento}
          value={filters.empresa}
        />
        {/* Admin sem instituição: o estado vazio da tabela pede a seleção (sem mensagem aqui,
            que desalinharia a linha de filtros). */}
      </TableFilter>
      <TableFilter label={t('mes.label')}>
        <CustomSelect
          entity='mes'
          onChange={(mes?: IOption<string>) => setFilter('mes', mes ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsMeses}
          value={filters.mes}
        />
      </TableFilter>
      <TableFilter label={t('ano.label')}>
        <CustomSelect
          entity='ano'
          onChange={(ano?: IOption<number>) => setFilter('ano', ano ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsAnos}
          value={filters.ano}
        />
      </TableFilter>
    </TableFilters>
  )
}
